import {AfterViewInit, Component, EventEmitter, Input, OnInit, ViewChild} from '@angular/core';
import { UntypedFormBuilder, Validators, FormGroup, NgForm, ValidatorFn } from '@angular/forms';
import { PatternValidator } from '../../shared/pattern-validator';
import {BsModalRef} from "ngx-bootstrap/modal";
import {ProfileService} from "../../services/profile/profile.service";
import {IsDirty} from "../../services/can-deactivate-guard/is-dirty.interface";
import {WarningModalService} from "../../services/warning-modal/warning-modal.service";
import {Observable, of} from "rxjs";
import {PermissionsService} from "../../services/permissions/permissions.service";
import {IdService} from "../../services/id/id.service";
import {AlertBannerService, AlertType} from "../../lib/alert-banner";

@Component({
  selector: 'app-password-update-modal',
  templateUrl: './password-update-modal.component.pug',
  styleUrls: ['./password-update-modal.component.scss'],
  standalone: false
})
export class PasswordUpdateModalComponent implements OnInit, AfterViewInit, IsDirty {

  public event: EventEmitter<any> = new EventEmitter();
  @Input() isPasswordReused: string = '';
  @Input() isCurrentPasswordCorrect: string = '';
  current_password = '';
  new_password = '';
  confirm_password = '';
  acceptedChars = "!\"#$%&'()*+,-./:;<=>?@[]^_`{|}~"
  messages = [];
  ent_9105_penetration_test_2025: Boolean = false;
  ent_10313_password_policy_and_mfa: boolean = false;
  policyLoaded = false;
  passwordPolicy: any = null;
  @ViewChild('setPasswordFormRef', {static: false}) passwordForm: any;
  setPasswordForm = this.fb.group({
    current_password: ['', [Validators.required]],
    new_password: ['', [Validators.required,
                    Validators.minLength(12),
                    PatternValidator.pattern(/\d/, { hasNumber: true}),
                    PatternValidator.pattern(/[A-Z]/, { hasUpperCase: true}),
                    PatternValidator.pattern(/[a-z]/, { hasLowerCase: true}),
                    PatternValidator.pattern(/[!"#$%&'()*+,-./:;<=>?@\[\]^_`{|}~]/, { hasSpecialChar: true})
                   ]
              ],
    confirm_password: ['', [Validators.required]],
  },
  {
    validator: PatternValidator.passwordMatchValidator
  });
  deprecated_setPasswordForm = this.fb.group({
    current_password: ['', [Validators.required]],
    new_password: ['', [Validators.required,
                    Validators.minLength(8),
                    PatternValidator.pattern(/\d/, { hasNumber: true}),
                    PatternValidator.pattern(/[A-Z]/, { hasUpperCase: true}),
                    PatternValidator.pattern(/[a-z]/, { hasLowerCase: true}),
                    PatternValidator.pattern(/[!"#$%&'()*+,-./:;<=>?@\[\]^_`{|}~]/, { hasSpecialChar: true})
                   ]
              ],
    confirm_password: ['', [Validators.required]],
  },
  {
    validator: PatternValidator.passwordMatchValidator
  });
  showValidationError = false;

  constructor(private fb: UntypedFormBuilder,
              private profile: ProfileService,
              public _bsModalRef: BsModalRef,
              private modal: WarningModalService,
              private permissions: PermissionsService,
              private idService: IdService,
              private alertList: AlertBannerService) {
  }

  ngOnInit(): void {
    this.ent_9105_penetration_test_2025 = this.permissions.isGlobalFeatureActivated('ENT-9105-PENETRATION-TEST-2025');
    this.ent_10313_password_policy_and_mfa = this.permissions.isFeatureActivated('ent-10313-password-policy-and-mfa');
    if (this.ent_10313_password_policy_and_mfa) {
      this.loadPasswordPolicy();
    }
  }

  get f() {
    if (this.ent_10313_password_policy_and_mfa) {
      return this.setPasswordForm?.controls;
    }
    return this.ent_9105_penetration_test_2025 ? this.setPasswordForm.controls : this.deprecated_setPasswordForm.controls;
  }

  get isSaveDisabled(): boolean {
    return !this.setPasswordForm || this.setPasswordForm.invalid || this.setPasswordForm.pristine;
  }

  private loadPasswordPolicy(): void {
    this.idService.getPasswordPolicy().subscribe(
      (policy) => {
        this.passwordPolicy = policy;
        this.buildFormFromPolicy(policy);
        this.policyLoaded = true;
        setTimeout(() => this.bindTogglePasswordListeners());
      },
      () => {
        this._bsModalRef.hide();
        this.alertList.append([{
          type: AlertType.DANGER,
          message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
          keepOnRouteChange: true
        }]);
      }
    );
  }

  private bindTogglePasswordListeners(): void {
    const togglers = document.querySelectorAll("[id='toggle-password']");
    // @ts-ignore
    for (const toggler of togglers) {
      toggler.addEventListener('click', this.toggleShowPassword);
    }
  }

  private buildFormFromPolicy(policy: any): void {
    const minLength = policy?.password_minimum_length ?? 12;
    const rules = policy?.complex_password_rules ?? {};
    const requireUpper = rules.force_upper_case ?? true;
    const requireLower = rules.force_lower_case ?? true;
    const requireNumbers = rules.force_numbers ?? true;
    const requireSpecial = rules.force_special_chars ?? true;

    const newPasswordValidators: ValidatorFn[] = [Validators.required, Validators.minLength(minLength)];

    if (requireNumbers) {
      newPasswordValidators.push(PatternValidator.pattern(/\d/, { hasNumber: true }));
    }
    if (requireUpper) {
      newPasswordValidators.push(PatternValidator.pattern(/[A-Z]/, { hasUpperCase: true }));
    }
    if (requireLower) {
      newPasswordValidators.push(PatternValidator.pattern(/[a-z]/, { hasLowerCase: true }));
    }
    if (requireSpecial) {
      newPasswordValidators.push(PatternValidator.pattern(/[!"#$%&'()*+,-./:;<=>?@\[\]^_`{|}~]/, { hasSpecialChar: true }));
    }

    this.setPasswordForm = this.fb.group({
      current_password: ['', [Validators.required]],
      new_password: ['', newPasswordValidators],
      confirm_password: ['', [Validators.required]],
    }, {
      validator: PatternValidator.passwordMatchValidator
    });
  }

  ngAfterViewInit(): void {
    let pwd_field_togglers = document.querySelectorAll("[id='toggle-password']");
    // @ts-ignore
    for(let pwd_field_toggler of pwd_field_togglers) {
      pwd_field_toggler.addEventListener("click", this.toggleShowPassword);
    }
  }

  cancel() {
    this.canDeactivate().subscribe(response => {
      if (response) {
        this.markAsPristine();
        this._bsModalRef.hide();
      }
    });
  }

  save() {
    this.messages = [];
    this.showValidationError = true;
    this.isCurrentPasswordCorrect = '';
    this.isPasswordReused = '';
    if (this.ent_10313_password_policy_and_mfa) {
      if (!this.setPasswordForm || this.setPasswordForm.invalid) {
        return;
      }
    } else {
      if (this.ent_9105_penetration_test_2025 ? this.setPasswordForm.invalid : this.deprecated_setPasswordForm.invalid) {
        return;
      }
    }
    this.event.emit({'password': this.f.current_password.value, 'new_password': this.f.new_password.value});
  }


  isDirty(): boolean {
    if (this.ent_10313_password_policy_and_mfa) {
      return this.setPasswordForm?.dirty ?? false;
    }
    return this.ent_9105_penetration_test_2025 ? this.setPasswordForm.dirty : this.deprecated_setPasswordForm.dirty;
  }

  markAsPristine() {
    if (this.ent_10313_password_policy_and_mfa) {
      this.setPasswordForm?.markAsPristine();
    } else {
      this.ent_9105_penetration_test_2025 ? this.setPasswordForm.markAsPristine() : this.deprecated_setPasswordForm.markAsPristine();
    }
  }

  canDeactivate(): Observable<boolean> {
    // If component is dirty, warn user
    if (this.isDirty()) {
      let title = $localize`Leave this page?`;
      let message = $localize`All information you have added will be lost if you navigate away.\n\nDo you want to leave this page?`;
      let cancelButton = $localize`Stay on page`;
      let agreeButton = $localize`Leave this page`;
      return this.modal.confirm(title= title, message= message, false, cancelButton, agreeButton, true);
    } else {
      return of(true);
    }
  }

  setMessages(newMessages) {
    this.messages = this.messages.concat(newMessages);
  }

  toggleShowPassword() {
    let passwordInput = document.querySelector("[name='current_password']");
    // @ts-ignore
    passwordInput.type = passwordInput?.type === "password" ? "text" : "password";
    passwordInput = document.querySelector("[name='new_password']");
    // @ts-ignore
    passwordInput.type = passwordInput?.type === "password" ? "text" : "password";
    passwordInput = document.querySelector("[name='confirm_password']");
    // @ts-ignore
    passwordInput.type = passwordInput?.type === "password" ? "text" : "password";
  }
}
